import { WritableAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { CustomFieldSelectedOption } from 'src/admin/customFields/entities/customFields.entity';
import { Autocomplete } from 'src/common/components/Inputs/Autocomplete/Autocomplete';
import { useCustomFieldFilter } from './hooks/useCustomFieldFilter';
import { useMemo, useState } from 'react';
import { filterOptions } from 'src/common/components/Inputs/inputUtils';
import { useDebounce } from 'src/common/useDebounce';
import { Option } from 'src/common/components/Inputs/Autocomplete/utils/models';
import { useCustomFieldHints } from './hooks/useCustomFieldHints';
import { CustomFields } from 'src/simInventory/models/SimProfile.model';
import { customFieldsHintsAtom } from 'src/atoms/hints/sim';

type CustomFieldSelectProps = {
  id: number;
  name: string;
  customFieldsFilterAtom: WritableAtom<CustomFields, [CustomFields], void>;
  customFieldsHintsAtom: typeof customFieldsHintsAtom;
  selectionOptions: CustomFieldSelectedOption[];
};

const mapCustomFieldIdsToCustomFieldOptions = (customFieldIds: string[], options: Option[]) => {
  return customFieldIds.reduce((cutomFieldsOptions, id) => {
    const cutomFieldsOption = options.find((option) => option.id === id);
    const customFieldOptionFound = !!cutomFieldsOption;

    if (customFieldOptionFound) {
      return [...cutomFieldsOptions, cutomFieldsOption];
    }

    return cutomFieldsOptions;
  }, [] as Option[]);
};

export const CustomFieldSelect = ({
  id,
  name,
  selectionOptions = [],
  customFieldsFilterAtom,
  customFieldsHintsAtom,
}: CustomFieldSelectProps) => {
  const { t } = useTranslation();
  const [serachFilter, setSearchFilter] = useState('');

  const debouncedSerachFilter = useDebounce(serachFilter, 500);
  const [customFieldIdsFilter, setCustomFieldIdsFilter] = useCustomFieldFilter(
    id,
    customFieldsFilterAtom,
  );

  const [customFieldHints, removeCustomField] = useCustomFieldHints(id, customFieldsHintsAtom);

  const options = useMemo(() => {
    const options = selectionOptions.map((o) => ({
      id: o.id?.toString() || '',
      name: o.label || '',
    }));

    return filterOptions(options, debouncedSerachFilter, false);
  }, [selectionOptions, debouncedSerachFilter]);

  const value = mapCustomFieldIdsToCustomFieldOptions(customFieldIdsFilter, options);

  const autocompleteHints = selectionOptions
    .filter((option) => customFieldHints?.includes(option.id?.toString()))
    .map((hint) => ({ ...hint, id: hint.id!.toString(), name: hint.label }));

  return (
    <Autocomplete
      data-testid={`customField-dropdown-${id}`}
      value={value}
      options={options}
      onChange={(values) => setCustomFieldIdsFilter(values.map((value) => value.id))}
      placeholder={`${t('simInventory.select')} ${name}`}
      onSearch={setSearchFilter}
      inputValue={serachFilter}
      hints={autocompleteHints}
      onHintRemove={(hint) => {
        if (hint.id) {
          removeCustomField(hint.id);
        }
      }}
    />
  );
};
